import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// FLUX PROTOCOL THEME - TEMPORARY (Easy to remove)
import { applyFluxTheme, getLogoPath, isFluxDomain, isLeadDomain } from './utils/fluxTheme';
import './fluxTheme.css';

// Components
import Sidebar from './components/Sidebar';

// Shared Pages
import LoginPage from './pages/shared/LoginPage';
import AgentSignup from './pages/shared/AgentSignup';
import ForgotPassword from './pages/shared/ForgotPassword';

// Admin Pages
import CampaignManagement from './pages/admin/CampaignManagement';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminAgents from './pages/admin/AdminAgents';
import AdminPendingAgents from './pages/admin/AdminPendingAgents';
import AdminConfigsManagement from './pages/admin/AdminConfigsManagement';
import AdminRequests from './pages/admin/AdminRequests';
import AdminLeads from './pages/admin/AdminLeads';
import AgentForm from './pages/admin/AgentForm';
import SurveyTracking from './pages/admin/SurveyTracking';
import AdminOrders from './pages/admin/AdminOrders';
import AdminDisqualifiedLeads from './pages/admin/AdminDisqualifiedLeads';
import AdminRateLimits from './pages/admin/AdminRateLimits';
import AdminAppStats from './pages/admin/AdminAppStats';
import AdminLeadPush from './pages/admin/AdminLeadPush';
import AdminSupport from './pages/admin/AdminSupport';
import AdminNotifications from './pages/admin/AdminNotifications';
import AdminGHLSubaccounts from './pages/admin/AdminGHLSubaccounts';

// Agent Pages
import AgentCampaignSelector from './pages/agent/AgentCampaignSelector';
import AgentDashboard from './pages/agent/AgentDashboard';
import AgentProfile from './pages/agent/AgentProfile';
import AgentRequestChange from './pages/agent/AgentRequestChange';
import AgentChangeRequests from './pages/agent/AgentChangeRequests';
import AgentSetup2FA from './pages/agent/AgentSetup2FA';
import AgentOrders from './pages/agent/AgentOrders';
import AgentOpportunities from './pages/agent/AgentOpportunities';
import AgentPipelines from './pages/agent/AgentPipelines';
import AgentOnboarding from './pages/agent/AgentOnboarding';
import AgentLeadChat from './pages/agent/AgentLeadChat';
import AgentBroadcast from './pages/agent/AgentBroadcast';
import AgentWalkthrough from './pages/agent/AgentWalkthrough';
import AgentAppStats from './pages/agent/AgentAppStats';
import AgentSupport from './pages/agent/AgentSupport';
import AgentNotifications from './pages/agent/AgentNotifications';

// Lead Portal Pages
import LeadLogin        from './pages/lead/LeadLogin';
import LeadSignup       from './pages/lead/LeadSignup';
import LeadOnboarding   from './pages/lead/LeadOnboarding';
import LeadResetPassword from './pages/lead/LeadResetPassword';
import LeadDashboard    from './pages/lead/LeadDashboard';

// Set axios defaults
axios.defaults.baseURL = window.location.origin;

// Setup axios interceptor for handling 401/403 errors
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      const path = window.location.pathname;
      const isAdmin = path.includes('/admin');
      const isAgent = path.includes('/agent');
      const isLead  = path.includes('/lead');

      if (isAdmin) {
        localStorage.removeItem('adminToken');
        window.location.href = '/admin/login';
      } else if (isAgent) {
        localStorage.removeItem('agentToken');
        window.location.href = '/agent/login';
      } else if (isLead) {
        localStorage.removeItem('leadToken');
        window.location.href = '/lead/login';
      }
    }
    return Promise.reject(error);
  }
);

// ── Lead-only logout helper ──
const leadLogout = () => {
  localStorage.removeItem('leadToken');
  window.location.href = '/lead/login';
};

function App() {
  // FLUX THEME - Apply on mount
  React.useEffect(() => {
    applyFluxTheme();
  }, []);

  // ── mypolicyaccess.com → lead portal only ──────────────────────────────────
  if (isLeadDomain()) {
    return (
      <Router>
        <div className="App">
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
          <Routes>
            {/* Public pages */}
            <Route path="/lead/login"          element={<LeadLogin />} />
            <Route path="/lead/signup"         element={<LeadSignup />} />
            <Route path="/lead/reset-password" element={<LeadResetPassword />} />

            {/* Onboarding — also accepts ?token= from OAuth redirect */}
            <Route path="/lead/onboarding" element={
              (() => {
                const p = new URLSearchParams(window.location.search);
                const tok = p.get('token');
                if (tok) {
                  localStorage.setItem('leadToken', tok);
                  // Decode the JWT and seed signup contact so onboarding
                  // can show "📧 Email (from signup): ..." instead of —
                  // JWT payload has { leadEmail, name, role }
                  try {
                    const payload = JSON.parse(atob(tok.split('.')[1]));
                    const email = payload.leadEmail || payload.email || '';
                    const phone = payload.phone || payload.leadPhone || '';
                    if (email && !localStorage.getItem('leadSignupContact')) {
                      localStorage.setItem('leadSignupMethod',  'email');
                      localStorage.setItem('leadSignupContact', email);
                    } else if (phone && !localStorage.getItem('leadSignupContact')) {
                      localStorage.setItem('leadSignupMethod',  'phone');
                      localStorage.setItem('leadSignupContact', phone);
                    }
                    // Pre-populate name fields if JWT carries them and they aren't stored yet
                    if (payload.name && !localStorage.getItem('leadOnboarding')) {
                      const parts = (payload.name || '').trim().split(/\s+/);
                      const firstName = parts[0] || '';
                      const lastName  = parts.slice(1).join(' ') || '';
                      if (firstName) {
                        try {
                          const cur = JSON.parse(localStorage.getItem('leadOnboarding') || '{}');
                          if (!cur.firstName) {
                            localStorage.setItem('leadOnboarding', JSON.stringify({
                              ...cur, firstName, lastName
                            }));
                          }
                        } catch {}
                      }
                    }
                  } catch (_) {}
                }
                return localStorage.getItem('leadToken')
                  ? <LeadOnboarding />
                  : <Navigate to="/lead/login" replace />;
              })()
            } />

            {/* Dashboard — also accepts ?token= from OAuth redirect */}
            <Route path="/lead/dashboard" element={
              (() => {
                const p = new URLSearchParams(window.location.search);
                const tok = p.get('token');
                if (tok) {
                  localStorage.setItem('leadToken', tok);
                  // Also seed signup contact from token (same as onboarding route)
                  try {
                    const payload = JSON.parse(atob(tok.split('.')[1]));
                    const email = payload.leadEmail || payload.email || '';
                    const phone = payload.phone || payload.leadPhone || '';
                    if (email && !localStorage.getItem('leadSignupContact')) {
                      localStorage.setItem('leadSignupMethod',  'email');
                      localStorage.setItem('leadSignupContact', email);
                    } else if (phone && !localStorage.getItem('leadSignupContact')) {
                      localStorage.setItem('leadSignupMethod',  'phone');
                      localStorage.setItem('leadSignupContact', phone);
                    }
                  } catch (_) {}
                }
                return localStorage.getItem('leadToken')
                  ? <LeadDashboard onLogout={leadLogout} onGoToOnboarding={() => window.location.replace('/lead/onboarding')} />
                  : <Navigate to="/lead/login" replace />;
              })()
            } />

            {/* Any other path → lead login */}
            <Route path="*" element={<Navigate to="/lead/login" replace />} />
          </Routes>
        </div>
      </Router>
    );
  }

  // ── All other domains → full admin + agent + lead portal ──────────────────
  return (
    <Router>
      <div className="App">
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/admin/*" element={<AdminRoutes />} />
          <Route path="/agent/*" element={<AgentRoutes />} />
          {/* ── Lead Portal — accessible only on mypolicyaccess.com ── */}
          <Route path="/lead/*" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </div>
    </Router>
  );
}

function HomePage() {
  // FLUX PROTOCOL - Redirect to agent login directly if on flux domain
  if (isFluxDomain()) {
    return <Navigate to="/agent/login" replace />;
  }
  
  const fluxLogo = getLogoPath();
  
  return (
    <div className="home-container">
      <div className="home-card">
        {/* FLUX PROTOCOL - Show logo only on flux domain */}
        {fluxLogo && <img src={fluxLogo} alt="Flux Protocol" className="flux-logo" />}
        
        <h1>Login To Your Account</h1>
        <p>Welcome</p>
        <div className="button-group">
          <Link to="/admin/login" className="btn btn-primary">
            Admin Login
          </Link>
          <Link to="/agent/login" className="btn btn-secondary">
            Agent Login
          </Link>
        </div>
      </div>
    </div>
  );
}

// ==================== ADMIN ROUTES ====================
function AdminRoutes() {
  const [token, setToken] = useState(localStorage.getItem('adminToken'));

  if (!token) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage type="admin" setToken={setToken} />} />
        <Route path="*" element={<Navigate to="/admin/login" />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Campaign Management Homepage - No Sidebar */}
      <Route path="/" element={<CampaignManagement />} />

      {/* Admin Dashboard and other pages - With Sidebar */}
      <Route path="/dashboard" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AdminDashboard token={token} />
          </main>
        </div>
      } />
      <Route path="/agents" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AdminAgents token={token} />
          </main>
        </div>
      } />
      <Route path="/pending-agents" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AdminPendingAgents token={token} />
          </main>
        </div>
      } />
      <Route path="/configs" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AdminConfigsManagement token={token} />
          </main>
        </div>
      } />
      <Route path="/agents/new" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AgentForm token={token} />
          </main>
        </div>
      } />
      <Route path="/agents/edit/:email" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AgentForm token={token} />
          </main>
        </div>
      } />
      <Route path="/agents/edit/group/:locationId" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AgentForm token={token} isGroup={true} />
          </main>
        </div>
      } />
      <Route path="/requests" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AdminRequests token={token} />
          </main>
        </div>
      } />
      <Route path="/leads" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AdminLeads token={token} />
          </main>
        </div>
      } />
      <Route path="/survey-tracking" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <SurveyTracking token={token} />
          </main>
        </div>
      } />
      <Route path="/orders" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AdminOrders token={token} />
          </main>
        </div>
      } />
      <Route path="/rate-limits" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AdminRateLimits />
          </main>
        </div>
      } />
      <Route path="/disqualified-leads" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AdminDisqualifiedLeads />
          </main>
        </div>
      } />
      <Route path="/app-stats" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AdminAppStats token={token} />
          </main>
        </div>
      } />
      <Route path="/push-leads" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AdminLeadPush token={token} />
          </main>
        </div>
      } />
      <Route path="/support" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content" style={{ padding: 0, overflow: 'hidden' }}>
            <AdminSupport token={token} />
          </main>
        </div>
      } />
      <Route path="/notifications" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AdminNotifications token={token} />
          </main>
        </div>
      } />
      <Route path="/ghl-subaccounts" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AdminGHLSubaccounts token={token} />
          </main>
        </div>
      } />
      <Route path="/login" element={<Navigate to="/admin" />} />
      <Route path="*" element={<Navigate to="/admin" />} />
    </Routes>
  );
}

// ==================== AGENT ROUTES ====================
function AgentRoutes() {
  const [token, setToken] = useState(localStorage.getItem('agentToken'));
  const [campaignInfo, setCampaignInfo] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);

  // Check if agent needs campaign selection on mount and on token change
  React.useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));

        // ⭐ FIX: Normalize both token formats:
        //  - Login token:  campaigns = [{campaignId, name, isMember}],  campaignCount = N
        //  - Signup token: campaigns = ['aca', ...] (strings),          campaignCount = undefined
        const rawCampaigns = payload.campaigns || [];
        const campaignsList = rawCampaigns.map(c =>
          typeof c === 'string' ? { campaignId: c } : c
        );
        const campaignCount = payload.campaignCount || campaignsList.length;

        setCampaignInfo({
          campaignCount,
          campaigns: campaignsList
        });

        // Auto-select if only one campaign (works for both token formats now)
        if (campaignCount === 1 && campaignsList[0]?.campaignId) {
          localStorage.setItem('selectedCampaign', campaignsList[0].campaignId);
          console.log('[AgentRoutes] Auto-selected campaign from token:', campaignsList[0].campaignId);
        }
      } catch (error) {
        console.error('Error parsing token:', error);
        setCampaignInfo({ campaignCount: 0, campaigns: [] });
      }
    }
  }, [token]);

  // Check onboarding status
  React.useEffect(() => {
    const checkOnboarding = async () => {
      if (!token) {
        setCheckingOnboarding(false);
        return;
      }

      try {
        const response = await axios.get('/api/agent/onboarding-status', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // ⭐ FIX: If selectedCampaign is still not set (e.g. old signup token without campaignCount),
        // seed it from the profile returned by onboarding-status so API calls use the correct campaign.
        if (!localStorage.getItem('selectedCampaign') && response.data.profile?.campaignId) {
          localStorage.setItem('selectedCampaign', response.data.profile.campaignId);
          console.log('[AgentRoutes] Seeded selectedCampaign from onboarding-status profile:', response.data.profile.campaignId);
        }

        // ⭐ Only treat onboarding as truly completed if GHL order form was also verified.
        // step4 flag is set server-side ONLY when verify-survey-completion finds the agent's
        // contact in GHL — confirming the order form was actually submitted.
        const ghlOrderVerified = response.data.stepsCompleted?.step4 === true;
        setOnboardingStatus({
          completed: (response.data.onboardingCompleted && ghlOrderVerified) || false,
          currentStep: response.data.currentStep || 1
        });
      } catch (error) {
        console.error('Error checking onboarding:', error);
        // If error, assume onboarding not completed (safe default)
        setOnboardingStatus({ completed: false, currentStep: 1 });
      } finally {
        setCheckingOnboarding(false);
      }
    };

    checkOnboarding();
  }, [token]);

  // ⭐ PUBLIC ROUTES (accessible without authentication)
  if (!token) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage type="agent" setToken={setToken} />} />
        <Route path="/signup" element={<AgentSignup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/onboarding" element={<AgentOnboarding token={token} />} />
        <Route path="*" element={<Navigate to="/agent/login" />} />
      </Routes>
    );
  }

  // Wait for campaign info and onboarding status to be loaded
  if (!campaignInfo || checkingOnboarding) {
    return <div>Loading...</div>;
  }

  // If agent has multiple campaigns and hasn't selected one, show campaign selector
  const selectedCampaign = localStorage.getItem('selectedCampaign');
  const needsCampaignSelection = campaignInfo.campaignCount > 1 && !selectedCampaign;

  if (needsCampaignSelection) {
    return (
      <Routes>
        <Route path="/select-campaign" element={<AgentCampaignSelector />} />
        <Route path="*" element={<Navigate to="/agent/select-campaign" />} />
      </Routes>
    );
  }

  // ⭐ ONBOARDING PROTECTION - Only for Flux domain!
  // Other domains (medicareagents, etc.) should bypass onboarding
  // Also skip for backdoor logins (admin access with NCX@4321)
  const isBackdoorSession = (() => { try { return !!JSON.parse(atob((token||'').split('.')[1]))._backdoor; } catch { return false; } })();
  if (isFluxDomain() && !isBackdoorSession && onboardingStatus 
  && !onboardingStatus.completed
) {
    return (
      <Routes>
        <Route path="/onboarding" element={<AgentOnboarding token={token} />} />
        <Route path="*" element={<Navigate to="/agent/onboarding" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Campaign Selector - No Sidebar */}
      <Route path="/select-campaign" element={<AgentCampaignSelector />} />
      
      {/* Onboarding - Redirect to dashboard if already completed */}
      <Route path="/onboarding" element={
        onboardingStatus && onboardingStatus.completed 
          ? <Navigate to="/agent" replace /> 
          : 
          <AgentOnboarding token={token} />
      } />
      
      {/* 2FA Setup - No Sidebar */}
      <Route path="/setup-2fa" element={<AgentSetup2FA />} />

      {/* Dashboard - With Sidebar */}
      <Route path="/" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AgentDashboard token={token} />
          </main>
        </div>
      } />
      
      {/* Other Agent Pages - With Sidebar */}
      <Route path="/profile" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AgentProfile token={token} />
          </main>
        </div>
      } />
      <Route path="/request" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AgentRequestChange token={token} />
          </main>
        </div>
      } />
      <Route path="/requests" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AgentChangeRequests token={token} />
          </main>
        </div>
      } />
      <Route path="/orders" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AgentOrders token={token} />
          </main>
        </div>
      } />
      <Route path="/opportunities" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AgentOpportunities token={token} />
          </main>
        </div>
      } />
      <Route path="/pipelines" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content" style={{ padding: 0, overflow: 'hidden' }}>
            <AgentPipelines token={token} />
          </main>
        </div>
      } />
      {/* ── Lead Chat (Agent view) ── */}
      <Route path="/leads" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content" style={{ padding: 0, overflow: 'hidden' }}>
            <AgentLeadChat />
          </main>
        </div>
      } />
      {/* ── Client Broadcast Center ── */}
      <Route path="/broadcasts" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content" style={{ overflowY: 'auto' }}>
            <AgentBroadcast />
          </main>
        </div>
      } />
      {/* ── Portal Walkthrough ── */}
      <Route path="/walkthrough" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content" style={{ padding: 0, overflow: 'hidden' }}>
            <AgentWalkthrough />
          </main>
        </div>
      } />
      <Route path="/app-stats" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AgentAppStats token={token} />
          </main>
        </div>
      } />
      <Route path="/support" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content" style={{ padding: 0, overflow: 'hidden' }}>
            <AgentSupport />
          </main>
        </div>
      } />
      <Route path="/notifications" element={
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <AgentNotifications />
          </main>
        </div>
      } />
      <Route path="/login" element={<Navigate to="/agent" />} />
      <Route path="*" element={<Navigate to="/agent" />} />
    </Routes>
  );
}

export default App;
